import { Box, Grid, Typography } from "@mui/material";
import { APIProvider } from "@vis.gl/react-google-maps";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { useContext } from "react";
import { BentoBrandSearchParameters } from "schemas/dashboard";

import { API_KEY } from "components/LocationAutocomplete/useAutoCompletePrediction";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import Collection from "features/Home/Sidebar/FavoritesList/Collections/SingleCollection";
import BrandSearch from "features/Influencer/Campaigns/NewCampaign/SharedComponents/BrandSearch";

interface Props {
  onNext?: (
    converted?: BentoBrandSearchParameters,
    numberSelected?: number,
  ) => void;
  onBack?: () => void;
  nextLoading?: boolean;
}

export default function SelectCollection({
  onNext,
  onBack,
  nextLoading,
}: Props) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { collections } = useContext(SavedBrandCollectionsContext);

  const {
    selectedCollection,
    setSelectedCollection,
    setSelectAll,
    setSelectedBrandIds,
  } = useContext(NewCampaignContext);

  const isAllSavedCollection = Boolean(
    selectedCollection && selectedCollection.id <= 0,
  );
  const savedBrandCollectionId =
    selectedCollection && selectedCollection.id > 0
      ? selectedCollection.id
      : undefined;

  const handleBackToCollections = () => {
    setSelectedCollection(null);
    setSelectAll(false);
    setSelectedBrandIds(new Set());
  };

  if (!selectedCollection) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontSize: 14, mb: 2 }}>
          Select Collection
        </Typography>
        <Box>
          {Object.values(collections).map((collection) => (
            <Collection
              key={collection.id}
              collection={collections[collection.id]}
              setSelectedCollection={() => {
                setSelectedCollection(collection);
                setSelectAll(false);
                setSelectedBrandIds(new Set());
              }}
            />
          ))}
        </Box>
        {!currentOrg?.isBrand && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 2 }}
          >
            <StyledLargeButton color="secondary" onClick={onBack}>
              Back
            </StyledLargeButton>
          </Grid>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: 14 }}>
          {selectedCollection.name}
        </Typography>
        <Box
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: "rgba(1,67,57,0.04)",
            fontSize: 12,
            color: "text.secondary",
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={{ fontSize: 12, color: "primary.main", mr: 0.75 }}
          />
          Showing unsent brands in this collection.{" "}
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
            Brands without contacts won&apos;t appear here.
          </Box>
        </Box>
      </Box>
      <APIProvider apiKey={API_KEY || ""}>
        <BrandSearch
          savedBrandCollectionId={savedBrandCollectionId}
          isAllSavedCollection={isAllSavedCollection}
          onNext={onNext}
          onBack={handleBackToCollections}
          nextLoading={nextLoading}
        />
      </APIProvider>
    </Box>
  );
}
